<template>
  <pe-drawer v-model="gameBar" :title="$t('gamification.title')" :width="600">
    <div class="text-neutral-900 dark:text-neutral-200 flex flex-col gap-8">
      <!-- Resumo -->
      <div class="flex h-[200px] items-center bg-cover" :style="{ backgroundImage: `url(${ImgGameBg})` }">
        <div class="flex w-full p-2 gap-4">
          <div class="stats">
            <img :src="ImgGamePoints" alt="points" width="24" height="24" />
            <div>
              <div class="text-sm font-semibold">{{ Number(userRank.data?.score || 0).toLocaleString('pt-BR') }}</div>
              <div class="text-xs text-neutral-600">{{ t('gamification.points') }}</div>
            </div>
          </div>

          <div class="stats">
            <img :src="ImgGameRank" alt="game rank" />
            <div>
              <div class="text-sm font-semibold">{{ userRank.data?.rank ? `${userRank.data?.rank} Lugar` : 'Indefinido' }}</div>
              <div class="text-xs text-neutral-600">{{ t('gamification.ranking') }}</div>
            </div>
          </div>

          <div class="stats">
            <img :src="ImgGameTrophy" alt="game trophy" />
            <div>
              <div class="text-sm font-semibold">{{ Number(userRank.data?.trophies || 0).toLocaleString('pt-BR') }} conquistas</div>
              <div class="text-xs text-neutral-600">{{ t('labels.total') }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Ranking -->
      <div>
        <div class="flex items-center justify-between mb-4">
          <div class="font-medium text-base">{{ t('gamification.ranking') }}</div>
          <RouterLink to="/gamificacao/jornada" class="text-xs font-medium" @click="gameBar = false">
            {{ t('gamification.fullJourney') }}
          </RouterLink>
        </div>

        <div v-if="usersRanks.pageStatus[1] === 'loading'">
          {{ t('feedback.loadingRanking') }}
        </div>
        <UiAlert v-else-if="usersRanks.pageStatus[1] === 'success' && !usersRanks.page[1].length" type="info">
          {{ t('gamification.status.emptyData') }}
        </UiAlert>
        <div v-else-if="usersRanks.pageStatus[1] === 'success' && usersRanks.page[1].length" class="flex flex-col gap-4">
          <UiGameRank
            v-for="(user) in usersRanks.page[1].slice(0, 3)"
            :key="user.score"
            :rank="user.rank"
            :name="user.name"
            :role="user.role"
            :score="user.score"
            :img="user.img"
            compact
          />
        </div>
      </div>

      <!-- Ultimas conquistas / Em andamento -->
      <div class="flex gap-8">
        <div class="w-full">
          <div class="font-medium text-base mb-4">{{ t('gamification.latestAchievements') }}</div>

          <div v-if="trophies.pageStatus[1] === 'loading'">{{ t('feedback.loadingAchievements') }}</div>
          <UiAlert v-if="trophies.pageStatus[1] === 'error'" type="danger">
            {{ t('gamification.status.error') }}
          </UiAlert>
          <UiAlert v-else-if="trophies.pageStatus[1] === 'success' && !trophies.page[1].length" type="info">
            {{ t('gamification.status.emptyData') }}
          </UiAlert>
          <div v-else-if="trophies.pageStatus[1] === 'success' && trophies.page[1].length" class="mb-8 flex flex-col gap-4">
            <UiGameAchievement
              v-for="(achv, index) in trophies.page[1].slice(0, 4)"
              :key="index"
              :data="achv"
              compact
              class="text-xs"
            />
          </div>
        </div>

        <div v-if="false" class="w-full">
          <div class="font-medium text-base mb-4">{{ t('labels.onGoing') }}</div>
          <div class="mb-8 flex flex-col gap-4">
            <div
              v-for="(item, index) in onGoing"
              :key="index"
              class="box-border h-14 px-2 flex items-center gap-4 rounded-md border border-solid border-neutral-200 bg-neutral-50 dark:border-neutral-400 dark:bg-neutral-700 dark:text-neutral-300"
            >
              <img :src="item.img" alt="" class="h-10 w-10 object-cover rounded-full" />
              <div>
                <div class="font-semibold mb-1">{{ item.type }}</div>
                <div>{{ item.name }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Ganhe mais pontos -->
      <div v-if="false">
        <div class="font-medium text-base-m-4">{{ t('gamification.earnMorePoints') }}</div>
        <div>
          <div
            v-for="(items, index) in earnMorePoints"
            :key="index"
          >
            <div class="flex items-center gap-4 mb-4">
              <div class="w-12 h-12 bg-neutral-200 rounded-full"></div>
              <div class="flex flex-col gap-1">
                <div class="font-semibold">Exemplo de atividade</div>
                <div class="text-xs text-neutral-600">Exemplo de atividade</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </pe-drawer>
</template>

<script setup lang="ts">
import { onMounted, watch } from 'vue'
import { useSessionStorage } from '@vueuse/core'
import { useGamificacaoStore, storeToRefs } from '@/stores'

import UiGameRank from '@/components/ui/gamification/rank.vue'
import UiGameAchievement from '@/components/ui/gamification/achievement.vue'
import UiAlert from '@/components/ui/alert.vue'

import ImgGamePoints from '@/assets/game/icon-points.svg'
import ImgGameRank from '@/assets/game/icon-rank.svg'
import ImgGameTrophy from '@/assets/game/icon-trophy.svg'
import ImgGameBg from '@/assets/game/game-bg.jpg'

import { useI18n } from 'vue-i18n'

const gameBar = useSessionStorage('game-bar', false)

const gamificationStore = useGamificacaoStore()
const { userRank, usersRanks, trophies } = storeToRefs(gamificationStore)

const { t } = useI18n()

const refresh = () => {

  if (gamificationStore.userRank.status === 'initial')
    gamificationStore.getMyRank()
}

watch(
  () => gameBar.value,
  (val) => {
    if (val) refresh()
  }
)

// Mock ongoing courses
const onGoing = Array.from({ length: 0 }, () => ({
  name: ['Exemplo', 'Exemplo 2', 'Exemplo 3', 'Exemplo 4'][Math.floor(Math.random() * 4)],
  type: ['Cruso', 'Formacao'][Math.floor(Math.random() * 2)],
  img: 'https://images.unsplash.com/photo-1598112154392-8125edb6e71d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80',
}))

// Mock earn more points
const earnMorePoints = Array.from({ length: 0 }, () => ({
  name: ['Exemplo', 'Exemplo 2', 'Exemplo 3', 'Exemplo 4'][Math.floor(Math.random() * 2)],
  type: ['Cruso', 'Formacao'][Math.floor(Math.random() * 4)],
  img: ['https://s3-alpha-sig.figma.com/img/dced/19bc/a3f212db452d0dcaccc4311208f9e40a?Expires=1684713600&Signature=HcJEdAvbvS4bztJCgvcy6VEoPHoCZepTJaEDQKDg0mctT59ffXsF2uy9Mqdx2j~16I-CIEIxijtGzRbeqr9KcrewUeTM1W5TqMUMco4KAAJGv76LAwlg5SNCeX5upYzaX3t3-f7rUXrUNXiKS9wfIjnlx34rUpNZQsIbFsJ6JywFax~y3M2f6-V4RNcvNbWLK5bMVKzE7~S9UnzC6RjgQInRoaj6heNXe5NlBoDteWtTtm8Cv0oRjVCxEfiQWT18SPGC9zg4mvSblKltm10jWruUv4TqNKeC7vfoUQs3zrmQhwjdv5oEQgXcO5eYn595ZlLBNqqqZpcpySM1hgR1Qg__&Key-Pair-Id=APKAQ4GOSFWCVNEHN3O4', 'https://s3-alpha-sig.figma.com/img/b144/f3b6/8f1f87ece59001f281129534163d311e?Expires=1684713600&Signature=oTRRC39jaH6TSYEDyKrC4l~DhQ4L1AB8dD2kXnJhR6lcMTQjLMMMsnhjbVFeVYQD6oLUwIiVhgYZbDpZbiv0yF5rxGEjTsmUuXPs8LTcRDTnB1cuOHyg5jFCqM6AZ9ZJoNHlJP3kp6Uro3JAH04K4wfU6GwDk2FRPEpgGahIXupny16tmqbQK6RCmgHrTEJrtiWNvQFptg7IhR1Vim6xDFQ2zGK44ulfPsJWriyM~SHTCAJsV8uY7Dyie8F5VJkRAo-zMo1d7Bn6CW13zpbvZs2ysxRimCFyLKjhQd07lB0mxVfV~D4i8cd1WBgwueUuh1NXNgCh7iifP47nrkNsoA__&Key-Pair-Id=APKAQ4GOSFWCVNEHN3O4'][Math.floor(Math.random() * 2)],
}))

onMounted(() => {
  refresh()
})
</script>

<style lang="scss" scoped>
.stats {
  @apply box-border flex w-full items-center gap-2 rounded border border-solid border-neutral-300 bg-white p-3 text-black shadow-lg;
}
</style>
